/**
 * What is actually inside one corpus release.
 *
 * The list says a release contains four documents. The question an operator has
 * when a finding cites it is WHICH four, at which version, fetched when - and
 * that was answerable only by reading the tables. A release is the thing a
 * compliance finding is accountable to; it has to be openable.
 *
 * The set is frozen. These are the documents the release was made from, read
 * through `corpus_release_documents` rather than re-derived from what is
 * current now, so a release made in August still lists the August versions
 * however many times those sources have been re-fetched since.
 */
import { useEffect, useState } from 'react';
import { ApiError, del, get, patch, post } from '../api.ts';
import { Empty, Pill, RowLink, Table, jaDate, jaNumber } from './ui.tsx';
import { Field } from './EditPanel.tsx';
import { Modal, ModalForm } from './Modal.tsx';
import { Textarea } from './Textarea.tsx';

interface ReleaseDocument {
  id: string;
  version_no: number;
  origin: string;
  origin_url: string | null;
  content_type: string;
  byte_size: number;
  sha256: string;
  published_on: string | null;
  fetched_at: string | null;
  status: string;
  source_id: string;
  publisher: string;
  title: string;
  kind: string;
  canonical_url: string | null;
  chunks: string;
}

interface DigestDraft {
  digest: string;
  lines: {
    line: string;
    citation: { chunkId: string; publisher: string; title: string; headingPath: string; excerpt: string };
  }[];
  dropped: number;
  provisions: number;
}

interface ReleaseFull {
  id: string;
  version_no: number;
  status: string;
  note: string;
  digest: string;
  created_at: string;
  activated_at: string | null;
  created_by_name: string | null;
  activated_by_name: string | null;
}

const KIND_LABEL: Record<string, string> = {
  law: '法令',
  guideline: 'ガイドライン',
  notice: '通知',
  internal: '社内基準',
};

export function ReleaseDetail({
  releaseId,
  onClose,
  onChanged,
}: {
  releaseId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<{
    release: ReleaseFull;
    documents: ReleaseDocument[];
    referencing: { reviews: string; rules: string };
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [note, setNote] = useState('');
  const [digest, setDigest] = useState('');
  /*
   * The drafted rules and what came back with them. Kept beside the field
   * rather than written into it: an operator has to be able to read a line
   * against the provision it came from before deciding it is a rule.
   */
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState<DigestDraft | null>(null);

  useEffect(() => {
    let live = true;
    void get<{
      release: ReleaseFull;
      documents: ReleaseDocument[];
      referencing: { reviews: string; rules: string };
    }>(`/admin/legal/releases/${releaseId}`)
      .then((body) => {
        if (!live) return;
        setData(body);
        setNote(body.release.note);
        setDigest(body.release.digest);
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : '読み込めませんでした'),
      );
    return () => {
      live = false;
    };
  }, [releaseId]);

  const release = data?.release;
  /*
   * Editable while a draft or while live. The document set is frozen either way
   * - that is what a finding cites - but the NAME is a label and the DIGEST is
   * an instruction to the writer, read fresh on every generation. Requiring a
   * new release to fix a line in it is what left this installation running with
   * an empty one.
   */
  const editable = release !== undefined && release.status !== 'ROLLED_BACK';
  /*
   * Everything except what is live. Deleting the ACTIVE release would leave the
   * product with no corpus while every screen still said the checks were
   * running; rolling it back first is one press.
   */
  const removable = release !== undefined && release.status !== 'ACTIVE';
  const reviews = Number(data?.referencing.reviews ?? 0);

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={release ? `リリース v${release.version_no}` : 'リリース'}
        caption={release?.note || undefined}
        wide
      >
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        {!release ? (
          <p className="muted small">読み込み中…</p>
        ) : (
          <>
            <dl className="factgrid" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
              <div className="factgrid__row">
                <dt>状態</dt>
                <dd>{release.status}</dd>
              </div>
              <div className="factgrid__row">
                <dt>作成</dt>
                <dd>
                  {jaDate(release.created_at)}
                  {release.created_by_name && `・${release.created_by_name}`}
                </dd>
              </div>
              <div className="factgrid__row">
                <dt>有効化</dt>
                <dd>
                  {release.activated_at ? (
                    <>
                      {jaDate(release.activated_at)}
                      {release.activated_by_name && `・${release.activated_by_name}`}
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </dd>
              </div>
              <div className="factgrid__row">
                <dt>資料</dt>
                <dd>{jaNumber(data.documents.length)} 件</dd>
              </div>
            </dl>

            <p className="form__section">このリリースに含まれる資料</p>
            {data.documents.length === 0 ? (
              <Empty>資料がありません。</Empty>
            ) : (
              <Table
                head={
                  <>
                    <th scope="col">資料</th>
                    <th scope="col">区分</th>
                    <th scope="col" className="num">
                      版
                    </th>
                    <th scope="col" className="num">
                      チャンク
                    </th>
                    <th scope="col">取得</th>
                    <th scope="col">原本</th>
                  </>
                }
              >
                {data.documents.map((doc) => (
                  // The question after "which documents" is "what is in this
                  // one", and that lives on the source's own page.
                  <tr key={doc.id} className="row-link">
                    <td>
                      <RowLink to={`/admin/legal/sources/${doc.source_id}`}>{doc.title}</RowLink>
                      <span className="muted small" style={{ display: 'block' }}>
                        {doc.publisher}
                      </span>
                    </td>
                    <td>{KIND_LABEL[doc.kind] ?? doc.kind}</td>
                    <td className="num">v{doc.version_no}</td>
                    <td className="num">{jaNumber(doc.chunks)}</td>
                    <td>{jaDate(doc.fetched_at)}</td>
                    <td>
                      {/*
                        The URL the bytes were taken from, which is not always
                        the source's canonical page - a guideline can be
                        published at one address and the PDF served from
                        another, and provenance is about where THIS copy came
                        from.
                      */}
                      {doc.origin_url ? (
                        <a href={doc.origin_url} target="_blank" rel="noopener noreferrer">
                          開く
                        </a>
                      ) : (
                        <Pill tone="muted">アップロード</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}

            {release.digest && (
              <>
                <p className="form__section">記事生成に渡す遵守事項</p>
                <pre className="pre">{release.digest}</pre>
              </>
            )}

            <div className="editpanel__actions">
              {/*
                Only a draft can be edited or discarded. An ACTIVE release is
                what every finding made under it cites, and a rolled-back one is
                the record of what was live before - both are history a review
                is accountable to.
              */}
              {editable && (
                <button className="btn btn--sm" type="button" onClick={() => setEditing(true)}>
                  名称・遵守事項を編集
                </button>
              )}
              {removable && (
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  onClick={() => setRemoving(true)}
                >
                  削除
                </button>
              )}
              {release.status === 'ACTIVE' && (
                <span className="muted small">
                  有効なリリースです。名称と遵守事項はこのまま編集できます（次の記事生成から反映）。
                  収録資料の入れ替えには新しいリリースが必要です。
                </span>
              )}
              {release.status === 'ROLLED_BACK' && (
                <span className="muted small">
                  ロールバック済みのリリースです。当時の判定の記録なので編集はできません。削除はできます。
                </span>
              )}
              <button className="btn btn--ghost btn--sm" type="button" onClick={onClose}>
                閉じる
              </button>
            </div>
          </>
        )}
      </Modal>

      {editing && release && (
        <ModalForm
          open
          onClose={() => setEditing(false)}
          title="リリースを編集"
          caption={
            release.status === 'ACTIVE'
              ? `v${release.version_no}（有効）：保存すると次の記事生成から反映されます`
              : `v${release.version_no}（下書き）`
          }
          submitLabel="保存する"
          onSubmit={async () => {
            await patch(`/admin/legal/releases/${release.id}`, { note, digest });
            setEditing(false);
            onChanged();
            onClose();
          }}
        >
          <Field
            label="名称・メモ"
            htmlFor="rel-note"
            span="full"
            hint="このリリースが何を変えたのかを書きます（例：症例写真の要件を追加）。一覧で探すときの手がかりです。"
          >
            <input
              id="rel-note"
              maxLength={1000}
              value={note}
              placeholder="例：2026年9月の改正を反映"
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>
          <Field
            label="記事生成に渡す遵守事項"
            htmlFor="rel-digest"
            span="full"
            hint="短い箇条書きに。記事を書く前にモデルへ渡す「常に守る規則」で、条文そのものではありません。"
          >
            <Textarea
              id="rel-digest"
              minRows={6}
              maxLength={4000}
              value={digest}
              onChange={(event) => setDigest(event.target.value)}
            />
            {/*
              Drafted from the release's own provisions, and only drafted. Each
              line cites the chunk it came from and quotes it verbatim; a line
              the model could not ground is dropped before it ever reaches this
              field. What it replaces is an operator retyping the guideline from
              memory, which is how the rules and the corpus drift apart.
            */}
            <div className="chips" style={{ marginTop: 8 }}>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                disabled={drafting}
                onClick={() => {
                  setDrafting(true);
                  void post<DigestDraft>(`/admin/legal/releases/${release.id}/digest-draft`, {})
                    .then((result) => {
                      setDraft(result);
                      if (result.digest) setDigest(result.digest);
                    })
                    .catch(() => setDraft(null))
                    .finally(() => setDrafting(false));
                }}
              >
                {drafting ? '条文を読み込み中…' : 'AIで下書きを作成'}
              </button>
              <span className="muted small">
                このリリースの条文から作成します。保存するまで反映されません。
              </span>
            </div>

            {draft && (
              <div style={{ marginTop: 10 }}>
                <p className="muted small" style={{ margin: 0 }}>
                  {jaNumber(draft.provisions)}件の条文から {jaNumber(draft.lines.length)}行。
                  {draft.dropped > 0 &&
                    `条文で裏づけられなかった ${jaNumber(draft.dropped)}行は破棄しました。`}
                </p>
                <ul className="feed" style={{ marginTop: 6 }}>
                  {draft.lines.map((line) => (
                    <li key={line.citation.chunkId + line.line}>
                      <span className="feed__dot" />
                      <span>
                        {line.line}
                        <span className="muted small" style={{ display: 'block' }}>
                          {line.citation.headingPath || line.citation.title}：
                          {line.citation.excerpt.slice(0, 90)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Field>
        </ModalForm>
      )}

      {removing && release && (
        <ModalForm
          open
          onClose={() => setRemoving(false)}
          title="このリリースを削除しますか"
          caption={
            release.status === 'DRAFT'
              ? `v${release.version_no}：まだ有効化されていないため、チェックの内容には影響しません。`
              : `v${release.version_no}：有効化されたことのあるリリースです。`
          }
          submitLabel="削除する"
          danger
          onSubmit={async () => {
            await del(`/admin/legal/releases/${release.id}`);
            setRemoving(false);
            onChanged();
            onClose();
          }}
        >
          <p className="field--full" style={{ margin: 0 }}>
            資料そのものは削除されません。このリリースという「まとまり」だけが消えます。
          </p>
          {/*
            The cost, in front of the person paying it. A review records which
            release it was judged against; deleting does not remove the review,
            it turns that record into 「不明」 - and nobody can be asked to weigh
            that without the number.
          */}
          {reviews > 0 && (
            <p className="alert alert--warn field--full" role="status">
              {jaNumber(reviews)}件の記事審査がこのリリースを参照しています。削除すると、
              その審査が<strong>どの資料で判定されたかの記録が失われます</strong>（審査結果そのものは残ります）。
            </p>
          )}
        </ModalForm>
      )}
    </>
  );
}
